import { useEffect } from "react"
import { KeyedMutator } from "swr"
import useSWRImmutable from "swr/immutable"

import { SUPPORT_REFER } from "../../../../../errors/errorMessages"
import { snackbar } from "../../../../../utils/general/generalUtils"
import { getClientProtected } from "../../../clients/clients"
import { dnoUserSuggestionMapper } from "../../../mappers/dnoInterface/dnoUserSuggestionMapper"
import { GET_USER_BY_ID } from "../../../urls/private"

export type User = {
    group: string
    username: string
    name: string
    initials: string
}

export interface IGetUserByIdType {
    isGetByIdLoading: boolean
    mutate: KeyedMutator<User>
}

/**
 * Custom hook for getting user by ID.
 * @param setValue - Function to set the value of the user.
 * @param userId - The ID of the user.
 */
const useGetAssigneeById = (
    setValue: (value: User | null) => void,
    userId: string
): IGetUserByIdType => {
    const { data, mutate, isLoading } = useSWRImmutable(
        userId ? GET_USER_BY_ID(userId) : undefined,
        async (url: string): Promise<User> => {
            try {
                const res = await getClientProtected(url)
                return dnoUserSuggestionMapper.mapSingleUser(res)
            } catch {
                snackbar(
                    `Unable to get application assignee. Please try again. ${SUPPORT_REFER}`,
                    "warning"
                )
                return {
                    group: "",
                    username: "",
                    name: "",
                    initials: "",
                }
            }
        }
    )

    useEffect(() => {
        if (!userId) {
            setValue(null)
            return
        }
        if (data) {
            if (userId) {
                setValue(data)
            } else {
                setValue(null)
            }
        }
    }, [data, userId, setValue])

    return {
        isGetByIdLoading: isLoading,
        mutate,
    }
}

export default useGetAssigneeById
